import { api } from "./client";

// Mirrors src/leadsniffer/api/serializers.py — keep in sync by hand.

export type VerificationExpected = "positive" | "negative";
export type VerificationVerdict = "pass" | "fail" | "error";
export type VerificationRunStatus = "running" | "succeeded" | "failed";

export interface VerificationSampleSummary {
  id: string;
  check_id: string;
  url: string;
  normalized_url: string;
  expected: VerificationExpected;
  note: string | null;
  created_at: string;
  cache_status: "ok" | "fetch_error" | null;
  cache_fetched_at: string | null;
  cache_unreachable_reason: string | null;
  cache_final_url: string | null;
}

export interface VerificationOutcome {
  sample_id: string;
  at: string;
  verdict: VerificationVerdict;
  matched_tags: string[];
  evidence: Array<Record<string, unknown>>;
  word_hits: Record<string, number> | null;
  unreachable_reason: string | null;
  harness_error: string | null;
}

export interface VerificationRunSummary {
  id: string;
  check_id: string;
  started_at: string;
  finished_at: string | null;
  status: VerificationRunStatus;
  total: number;
  completed: number;
  sample_ids: string[];
  outcomes: VerificationOutcome[];
  error: string | null;
}

export interface VerificationView {
  samples: VerificationSampleSummary[];
  latest_run: VerificationRunSummary | null;
}

export interface BulkAddResult {
  created: string[];
  skipped_duplicates: string[];
  rejected: Array<{ url: string; reason: string }>;
}

export const getVerification = (checkId: string) =>
  api<VerificationView>(`/api/checks/${checkId}/verification`);

export const addVerificationSample = (
  checkId: string,
  body: { url: string; expected: VerificationExpected; note?: string | null },
) =>
  api<VerificationSampleSummary>(
    `/api/checks/${checkId}/verification/samples`,
    { method: "POST", body: JSON.stringify(body) },
  );

export const bulkAddVerificationSamples = (
  checkId: string,
  body: { expected: VerificationExpected; urls: string[] },
) =>
  api<BulkAddResult>(`/api/checks/${checkId}/verification/samples/bulk`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateVerificationSample = (
  checkId: string,
  sampleId: string,
  body: { expected?: VerificationExpected; note?: string | null },
) =>
  api<VerificationSampleSummary>(
    `/api/checks/${checkId}/verification/samples/${sampleId}`,
    { method: "PUT", body: JSON.stringify(body) },
  );

export const deleteVerificationSample = (checkId: string, sampleId: string) =>
  api<void>(`/api/checks/${checkId}/verification/samples/${sampleId}`, {
    method: "DELETE",
  });

export const refreshVerificationSample = (checkId: string, sampleId: string) =>
  api<void>(
    `/api/checks/${checkId}/verification/samples/${sampleId}/refresh`,
    { method: "POST" },
  );

export const startVerification = (
  checkId: string,
  body: { sample_ids?: string[] } = {},
) =>
  api<{ run_id: string }>(`/api/checks/${checkId}/verification/runs`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const getVerificationRun = (checkId: string, runId: string) =>
  api<VerificationRunSummary>(
    `/api/checks/${checkId}/verification/runs/${runId}`,
  );
